<div class="files-page">
  <!-- Header Section -->
  <div class="files-header">
    <div class="header-content">
      <h2>File Management</h2>

      <!-- Storage Quota Display -->
      <div class="storage-quota" *ngIf="storageQuota && storageQuota.hasQuota">
        <div class="quota-info">
          <span class="quota-text">
            {{ formatFileSize(storageQuota.usedBytes) }} / {{ formatFileSize(storageQuota.maxBytes) }}
          </span>
          <div class="quota-bar">
            <div class="quota-fill" [style.width.%]="getStorageUsedPercentage()"></div>
          </div>
          <span class="quota-percentage">{{ getStorageUsedPercentage() }}%</span>
        </div>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="header-actions">
      <!-- Actions moved to context menu -->
    </div>
  </div>

  <!-- Tab Navigation -->
  <mat-tab-group [(selectedIndex)]="currentTabIndex" (selectedTabChange)="onTabIndexChange($event)">
    <mat-tab *ngFor="let tab of availableTabs" [label]="tab.label">
      <ng-template mat-tab-label>
        <mat-icon>{{ tab.icon }}</mat-icon>
        {{ tab.label }}
      </ng-template>
    </mat-tab>
  </mat-tab-group>

  <!-- File Actions Area -->
  <div class="file-actions-area" *ngIf="!isLoading">
    <div class="action-buttons">
      <button mat-raised-button color="primary"
              (click)="onUploadToggle()"
              [disabled]="!canUploadToCurrentTab()">
        <mat-icon>cloud_upload</mat-icon>
        Upload Files
      </button>

      <button mat-raised-button color="accent"
              (click)="onCreateFolderToggle()"
              [disabled]="!canCreateFolder()">
        <mat-icon>create_new_folder</mat-icon>
        New Folder
      </button>
    </div>

    <!-- File Upload Area -->
    <div class="upload-section" *ngIf="showUploadArea">
      <div class="upload-dropzone"
           (dragover)="onDragOver($event)"
           (drop)="onFileDrop($event)"
           [class.drag-active]="isDragActive">
        <div class="upload-content">
          <mat-icon class="upload-icon">cloud_upload</mat-icon>
          <p>Drag and drop files here or click to choose files</p>
          <input type="file"
                 #fileInput
                 multiple
                 (change)="onFileSelected($event)"
                 style="display: none">
          <button mat-raised-button color="accent" (click)="fileInput.click()">
            Choose Files
          </button>
        </div>
      </div>

      <!-- Upload Progress -->
      <div class="upload-progress-section" *ngIf="uploadingFiles.size > 0">
        <div class="upload-progress-item" *ngFor="let fileName of uploadingFileNames">
          <div class="progress-header">
            <span class="file-name">{{ fileName }}</span>
            <span class="progress-percentage">{{ uploadProgress[fileName] || 0 }}%</span>
          </div>
          <mat-progress-bar
            mode="determinate"
            [value]="uploadProgress[fileName] || 0"
            class="progress-bar">
          </mat-progress-bar>
        </div>
      </div>

      <button mat-button (click)="onUploadToggle()" class="close-action" [disabled]="uploadingFiles.size > 0">
        <mat-icon>close</mat-icon>
        Cancel
      </button>
    </div>

    <!-- Create Folder Section -->
    <div class="create-folder-section" *ngIf="showCreateFolder">
      <div class="create-folder-content">
        <mat-form-field appearance="outline" class="folder-name-field">
          <mat-label>Folder name</mat-label>
          <input matInput
                 [(ngModel)]="newFolderName"
                 (keyup.enter)="createFolder()"
                 placeholder="Enter folder name">
        </mat-form-field>
        <button mat-raised-button color="primary" (click)="createFolder()" [disabled]="!newFolderName.trim()">
          Create
        </button>
        <button mat-button (click)="onCreateFolderToggle()">
          Cancel
        </button>
      </div>
    </div>
  </div>

  <!-- Search and Controls -->
  <div class="file-controls" *ngIf="!isLoading">
    <div class="controls-left">
      <!-- Breadcrumb Navigation -->
      <nav class="breadcrumb" *ngIf="breadcrumbs.length > 1">
        <button mat-button
                *ngFor="let crumb of breadcrumbs; let last = last"
                (click)="onFolderNavigate(crumb.path)"
                [class.active]="last">
          {{ crumb.name }}
          <mat-icon *ngIf="!last">chevron_right</mat-icon>
        </button>
      </nav>
    </div>

    <div class="controls-right">
      <!-- Search Bar -->
      <mat-form-field appearance="outline" class="search-field">
        <mat-label>Search files</mat-label>
        <input matInput
               [(ngModel)]="searchQuery"
               (ngModelChange)="onSearchChange($event)"
               placeholder="Type to search...">
        <mat-icon matSuffix>search</mat-icon>
      </mat-form-field>
    </div>
  </div>

  <!-- Loading State -->
  <div class="loading-container" *ngIf="isLoading">
    <mat-spinner diameter="40"></mat-spinner>
    <p>Loading files...</p>
  </div>

  <!-- Error State -->
  <div class="error-container" *ngIf="error && !isLoading">
    <mat-icon color="warn" class="error-icon">error</mat-icon>
    <p>{{ error }}</p>
    <button mat-raised-button color="primary" (click)="loadFiles()">
      <mat-icon>refresh</mat-icon>
      Try Again
    </button>
  </div>

  <!-- Files Display -->
  <div class="files-container" *ngIf="!isLoading && !error">
    <div class="files-list">
      <div class="list-header">
        <div class="col-icon"></div>
        <div class="col-name">Name</div>
        <div class="col-size">Size</div>
        <div class="col-date">Modified</div>
        <div class="col-actions">Actions</div>
      </div>

      <div class="file-row"
           *ngFor="let item of filteredItems"
           (click)="onFileSelect(item)"
           (dblclick)="item.isFolder ? onFolderNavigate(item.path) : null"
           [class.selected]="isFileSelected(item)"
           [class.folder]="item.isFolder">
        <div class="col-icon">
          <mat-icon class="file-icon">{{ getFileIcon(item) }}</mat-icon>
        </div>
        <div class="col-name">
          <span [title]="item.name">{{ item.name }}</span>
        </div>
        <div class="col-size">
          <span *ngIf="!item.isFolder">{{ formatFileSize(item.fileData?.size || 0) }}</span>
          <span *ngIf="item.isFolder" class="folder-indicator">--</span>
        </div>
        <div class="col-date">
          {{ (item.isFolder ? item.folderData?.createdAt : item.fileData?.uploadedAt) | date:'short' }}
        </div>
        <div class="col-actions">
          <button mat-icon-button [matMenuTriggerFor]="fileMenu" (click)="$event.stopPropagation()">
            <mat-icon>more_vert</mat-icon>
          </button>
          <mat-menu #fileMenu="matMenu">
            <!-- Primary Actions -->
            <!-- File download -->
            <button mat-menu-item (click)="downloadFileEnhanced(convertToFileMetadata(item)!)" *ngIf="!item.isFolder">
              <mat-icon>download</mat-icon>
              <span>Download</span>
            </button>

            <!-- Folder navigation -->
            <button mat-menu-item (click)="onFolderNavigate(item.path)" *ngIf="item.isFolder">
              <mat-icon>folder_open</mat-icon>
              <span>Open Folder</span>
            </button>

            <mat-divider *ngIf="canMoveFile(convertToFileMetadata(item)!) || canDeleteFile(convertToFileMetadata(item)!)"></mat-divider>

            <!-- Management Actions -->
            <!-- Move action for both files and folders -->
            <button mat-menu-item (click)="moveFile(convertToFileMetadata(item)!)" *ngIf="canMoveFile(convertToFileMetadata(item)!)">
              <mat-icon>drive_file_move</mat-icon>
              <span>Move</span>
            </button>

            <!-- Delete action for both files and folders -->
            <button mat-menu-item (click)="deleteFile(convertToFileMetadata(item)!)" *ngIf="canDeleteFile(convertToFileMetadata(item)!)" class="warn-action">
              <mat-icon>delete</mat-icon>
              <span>Delete</span>
            </button>
          </mat-menu>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div class="empty-state" *ngIf="filteredItems.length === 0">
      <mat-icon class="empty-icon">{{ getEmptyStateIcon() }}</mat-icon>
      <h3>No files or folders found</h3>
      <p *ngIf="searchQuery">Try adjusting your search terms.</p>
      <p *ngIf="!searchQuery">This folder is empty. Start by uploading files or creating folders.</p>
    </div>
  </div>
</div>
